import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import PageHeader from '../../../Components/PageHeader';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  getVendorBatch,
  markVendorBatchSent,
} from '../../../endpoints/chapterSupplies';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

export default function VendorOrderBatchDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [actionBusy, setActionBusy] = React.useState(false);
  const { data, isLoading, error } = useApiGet(
    ['chapter-supplies-vendor-batch', id],
    () => getVendorBatch(id!),
    { enabled: !!id, retry: 1 }
  );

  const handleMarkSent = async () => {
    if (!id) return;
    setActionError(null);
    setActionBusy(true);
    try {
      await markVendorBatchSent(id);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-vendor-batch', id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-vendor-batches'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['chapter-supplies-management-orders'],
        }),
      ]);
    } catch (e) {
      setActionError(
        e instanceof Error ? e.message : 'Unable to mark batch sent.'
      );
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={data ? `Vendor batch ${data.id}` : 'Vendor batch'}
        subtitle="Review included orders and line personalization before sending work to the vendor."
        leftDom={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() =>
              navigate('/chapter-supplies-management/vendor-batches')
            }
          >
            Back to vendor batches
          </Button>
        }
        rightDom={
          data?.batch_status !== 'sent' ? (
            <Button
              variant="contained"
              disabled={actionBusy}
              onClick={() => void handleMarkSent()}
            >
              {actionBusy ? 'Marking…' : 'Mark sent to vendor'}
            </Button>
          ) : undefined
        }
      />

      {isLoading && <Typography>Loading vendor batch…</Typography>}
      {error && <Alert severity="error">Unable to load vendor batch.</Alert>}
      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}

      {data && (
        <Stack spacing={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Batch metadata
              </Typography>
              <Typography variant="body2">
                Vendor: {data.vendor_name || data.vendor_id}
                <br />
                Status: {data.batch_status}
                <br />
                Created: {new Date(data.created_at).toLocaleString()}
                <br />
                Orders: {data.order_count}
                <br />
                Lines: {data.line_count}
                {data.sent_at ? (
                  <>
                    <br />
                    Sent: {new Date(data.sent_at).toLocaleString()}
                  </>
                ) : null}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Future PDF hooks
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Orders PDF endpoint: {data.orders_pdf_endpoint}
                <br />
                Labels PDF endpoint: {data.labels_pdf_endpoint}
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Orders
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Placed</TableCell>
                    <TableCell>Purchaser</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Ship to</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.orders.map((order) => (
                    <TableRow
                      hover
                      key={order.order_id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() =>
                        navigate(
                          `/chapter-supplies-management/orders/${order.order_id}`
                        )
                      }
                    >
                      <TableCell>
                        {order.order_number != null
                          ? `#${order.order_number}`
                          : order.order_id}
                      </TableCell>
                      <TableCell>
                        {new Date(order.placed_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{order.purchaser_display_name}</TableCell>
                      <TableCell>{order.order_status_name}</TableCell>
                      <TableCell>{order.ship_to_name}</TableCell>
                      <TableCell align="right">
                        {formatPrice(order.total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Batch lines
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Custom text</TableCell>
                    <TableCell>Personalization</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lines.map((line) => (
                    <TableRow key={line.order_line_item_id}>
                      <TableCell>
                        {line.order_number != null
                          ? `#${line.order_number}`
                          : line.order_id}
                      </TableCell>
                      <TableCell>
                        {line.item_name}
                        {line.sku ? (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {line.sku}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">{line.quantity}</TableCell>
                      <TableCell align="right">
                        {formatPrice(line.unit_price)}
                      </TableCell>
                      <TableCell>
                        {line.custom_text_lines &&
                        line.custom_text_lines.length > 0
                          ? line.custom_text_lines.join(', ')
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {line.personalization_display_name ||
                        line.personalization_first_name ? (
                          <>
                            {line.personalization_display_name
                              ? `Display: ${line.personalization_display_name}`
                              : ''}
                            {line.personalization_display_name &&
                            line.personalization_first_name
                              ? ' | '
                              : ''}
                            {line.personalization_first_name
                              ? `First: ${line.personalization_first_name}`
                              : ''}
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
}
